import React, { useCallback, useState } from 'react'
import { ReactComponent as DockPenIcon } from 'assets/icons/dock_pen.svg'
import { ReactComponent as DockIcon } from 'assets/icons/dock.svg'
import { ReactComponent as MessageIcon } from 'assets/icons/message.svg'
import SendMessageModal from 'pages/Students/components/SendMessageModal/SendMessageModal'
import { buildAssignmentReminderMessage } from '../../../utils/assignmentReminderMessages'
import { recordReminderSent } from '../../../utils/assignmentReminderCooldown'
import AssignmentReminderAction from './AssignmentReminderAction'
import s from './Assignments.module.scss'

const STATUS_META = {
	not_started: { label: 'Not started', tone: 'idle' },
	in_progress: { label: 'In progress', tone: 'progress' },
	completed: { label: 'Completed', tone: 'done' },
}

const TYPE_META = {
	essay: { label: 'Essay', Icon: DockPenIcon },
	document: { label: 'Document', Icon: DockIcon },
	lor: { label: 'LoR', Icon: MessageIcon },
}

const normalize = value =>
	String(value || 'not_started')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '_')
		.replace(/-/g, '_')

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatDate = value => {
	if (!value) return ''
	const dt = new Date(value)
	if (Number.isNaN(dt.getTime())) return String(value)
	return `Due ${MONTHS[dt.getMonth()]} ${dt.getDate()}`
}

const mapStatus = value => {
	const key = normalize(value)
	if (key === 'completed' || key === 'done') return 'completed'
	if (key === 'in_progress' || key === 'pending') return 'in_progress'
	return 'not_started'
}

const emptyReminderState = () => ({
	open: false,
	message: '',
	students: [],
	assignmentId: '',
})

const Assignments = ({ row }) => {
	const items = row?.assignments || []
	const [reminderModal, setReminderModal] = useState(emptyReminderState)
	const [cooldownRefreshKey, setCooldownRefreshKey] = useState(0)

	const openReminderModal = useCallback(
		(item, statusKey) => {
			const studentId = String(row?.studentId || '').trim()
			if (!studentId) return

			const message = buildAssignmentReminderMessage({
				status: statusKey,
				studentName: row?.studentName,
				applicationName: row?.university,
				deadline: item.dueDate || item.startDate,
			})

			setReminderModal({
				open: true,
				message,
				assignmentId: String(item.id || '').trim(),
				students: [
					{
						id: studentId,
						name: row?.studentName || 'Student',
					},
				],
			})
		},
		[row?.studentId, row?.studentName, row?.university],
	)

	const closeReminderModal = useCallback(() => {
		setReminderModal(emptyReminderState())
	}, [])

	const handleReminderSent = useCallback(async () => {
		if (reminderModal.assignmentId) {
			recordReminderSent(reminderModal.assignmentId)
			setCooldownRefreshKey(value => value + 1)
		}
	}, [reminderModal.assignmentId])

	return (
		<section className={s.wrap}>
			<header className={s.header}>
				<h4 className={s.title}>Assignments</h4>
				<span className={s.count}>{items.length}</span>
			</header>

			{!items.length ? (
				<p className={s.empty}>No assignments yet</p>
			) : (
				<ul className={s.list}>
					{items.map(item => {
						const statusKey = mapStatus(item.status)
						const meta = STATUS_META[statusKey] || STATUS_META.not_started
						const type = TYPE_META[normalize(item.type)] || TYPE_META.essay
						const Icon = type.Icon
						const dueLabel = formatDate(item.dueDate || item.startDate)
						const assignmentId = String(item.id || '').trim()

						return (
							<li key={assignmentId || `${item.type}-${item.title}`} className={s.item}>
								<span className={s.icon}>
									<Icon />
								</span>

								<div className={s.main}>
									<span className={s.itemTitle}>
										{type.label}: {item.title}
									</span>
									{dueLabel && <span className={s.due}>{dueLabel}</span>}
								</div>

								<span className={`${s.pill} ${s[meta.tone]}`}>{meta.label}</span>

								<AssignmentReminderAction
									assignmentId={assignmentId}
									statusKey={statusKey}
									cooldownRefreshKey={cooldownRefreshKey}
									onRemind={() => openReminderModal(item, statusKey)}
								/>
							</li>
						)
					})}
				</ul>
			)}

			<SendMessageModal
				isOpen={reminderModal.open}
				onClose={closeReminderModal}
				selectedStudents={reminderModal.students}
				initialMessage={reminderModal.message}
				onAfterSendSuccess={handleReminderSent}
			/>
		</section>
	)
}

export default Assignments
